import { NextPage } from 'next';
import Head from 'next/head';
import { useState } from 'react';
import { getWhiteLabelPageTitle } from 'utils/whiteLabelConfig';

import AuthorizationCheck from 'components/container-components/allowedRoutes';
import Breadcrumbs from 'components/app-components/ui/breadcrumbs';
import { AdminRoutes } from 'routes';
import withLayout from 'components/container-components/layout/withLayout';
import { FileUpload } from 'components/generic-components/fileUpload/FileUpload';
import { UploadIcon } from 'icons/UploadIcon';
import Button from 'components/app-components/ui/button';
import { uploadFileService, uploadMultipleFilesService } from 'services/filesApi';
import theraToast from 'components/generic-components/toast/TheraToast';
import { formatErrorMessage, logError } from 'utils/error';
import { useUser } from 'context/user';
import { getPlural } from 'utils/string';
import { createRenamedFile, generateUniqueFileName } from 'utils/files';

const SecureUpload: NextPage = () => {
  const { currentCompany, isLoading: isUserLoading } = useUser();
  const [files, setFiles] = useState<File[]>([]);
  const [fileIDs, setFileIDs] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      // Get existing file names
      const existingFileNames = files.map((f) => f.name);

      // Generate unique filename if needed
      const uniqueFileName = generateUniqueFileName(file.name, existingFileNames);

      // Create new file with unique name if it was changed
      const fileToUpload = uniqueFileName !== file.name ? createRenamedFile(file, uniqueFileName) : file;

      const response = await uploadFileService(fileToUpload);
      setFiles((prevFiles) => [...prevFiles, fileToUpload]);
      setFileIDs((prevIds) => [...prevIds, response.s3key]);
      theraToast.success(`File ${fileToUpload.name} uploaded!`);
    } catch (error) {
      const errorMessage = formatErrorMessage(error, 'Failed to upload file');
      setUploadErrorMessage(errorMessage);
      logError(error, {
        source: 'SecureUpload',
        isToastError: true,
        category: 'document_upload',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmitFiles = async () => {
    if (!currentCompany?.companyID || !currentCompany?.companyName) {
      logError(new Error('Company information is not available'));
      return;
    }

    setIsSubmitting(true);
    try {
      // Use the new multiple file upload service with company info
      await uploadMultipleFilesService(files, currentCompany.companyID);

      theraToast.success('Files submitted!');
      // Reset the form after successful submission
      setFiles([]);
      setFileIDs([]);
    } catch (error) {
      const errorMessage = formatErrorMessage(error, 'Failed to submit files');
      theraToast.error({
        message: errorMessage,
        error: error,
        options: {
          id: 'failed-to-submit-files',
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Head>
        <title>{getWhiteLabelPageTitle('Thera Payroll | Secure Upload')}</title>
      </Head>
      <div className="pb-16 pt-4">
        <Breadcrumbs
          crumbs={[
            { href: AdminRoutes.Dashboard, label: 'Dashboard' },
            {
              label: 'Secure Upload',
            },
          ]}
        />
        <div className="mb-4 flex items-center">
          <h3 className="text-2xl font-medium">Secure Upload</h3>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h4 className="mb-4 text-xl font-medium">Upload Documents Securely</h4>
          <p className="mb-6 text-gray-600">
            Use this to securely upload documents. Each file must be under 5MB, with a total limit of 20MB. If your
            files exceed 20MB, please upload in batches.
          </p>

          <FileUpload
            label="Upload Documents"
            file={null}
            setFile={() => {}}
            uploadAreaText="Click to upload or drag and drop files"
            uploadAreaIcon={() => <UploadIcon />}
            handleFileUpload={handleFileUpload}
            error={uploadErrorMessage}
            isUploading={isUploading}
            setUploadErrorMessage={setUploadErrorMessage}
            acceptedFileTypes={[
              'application/pdf',
              'text/csv',
              'application/xml',
              'text/xml',
              'text/plain',
              'application/msword',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'application/vnd.ms-excel',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ]}
            hasMultipleUploads={true}
            maxSizeInMb={5}
            maxTotalSizeInMb={20}
            files={files}
            setFiles={setFiles}
            fileIDs={fileIDs}
            setFileIDs={setFileIDs}
          />

          <div className="mt-6">
            <Button
              variant="primary"
              onClick={handleSubmitFiles}
              className="w-full"
              loading={isSubmitting || isUserLoading}
              disabled={isSubmitting || !currentCompany?.companyID || files.length === 0}
            >
              Submit {getPlural(files.length, 'Document')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthorizationCheck(withLayout(SecureUpload, { hasSidebar: false, isNarrowWidth: true }));
